import archivePool from './archive-pool.json';
import type { Subject } from '../../shared/types';
import type { GeneratedQuestion } from '../generators/math/types';

export type ArchiveSource = 'crawled' | 'simulated' | 'manual';

export interface ArchiveQuestion {
  subject: Subject;
  kp: string;
  kind: 'mcq' | 'fill';
  prompt: string;
  options?: (string | number)[];
  correctAnswer: number | string;
  explanation: string;
  acceptedAnswers?: string[];
  surfaceVariant?: string;
  /** Where this question came from. Defaults to 'manual' when absent. */
  source?: ArchiveSource;
  /** For simulated questions: references the id of the original crawled question */
  sourceRefId?: string;
  /** Human-readable provenance label shown in the UI, e.g. "臺北市 110 學年度上學期月考" */
  sourceLabel?: string;
}

export interface ArchiveQuestionWithMeta {
  /** Full GeneratedQuestion fields (inlined to avoid union-extend restriction) */
  kind: 'mcq' | 'fill';
  prompt: string;
  explanation: string;
  kp: string;
  options?: (string | number)[];
  correctAnswer: number | string;
  acceptedAnswers?: string[];
  /** Archive-specific metadata */
  source: ArchiveSource;
  sourceLabel?: string;
}

/**
 * Load all archive-pool questions with their source metadata.
 */
export function loadAllArchive(): ArchiveQuestionWithMeta[] {
  const all = (archivePool as { questions: ArchiveQuestion[] }).questions;
  return all.map(q => mapToMeta(q));
}

/**
 * Load archive questions filtered by subject, mapped to GeneratedQuestion.
 * Returns an empty array when the pool has no questions for that subject.
 */
export function loadArchiveBySubject(subject: Subject): GeneratedQuestion[] {
  const all = (archivePool as { questions: ArchiveQuestion[] }).questions;
  return all
    .filter(q => q.subject === subject)
    .map(q => mapToGenerated(q));
}

/**
 * Load archive questions filtered by subject and optionally by source.
 */
export function loadArchiveFiltered(opts: {
  subject?: Subject;
  source?: ArchiveSource | 'all';
}): ArchiveQuestionWithMeta[] {
  const all = (archivePool as { questions: ArchiveQuestion[] }).questions;
  return all
    .filter(q => {
      if (opts.subject && q.subject !== opts.subject) return false;
      if (opts.source && opts.source !== 'all') {
        const qSource: ArchiveSource = q.source ?? 'manual';
        if (qSource !== opts.source) return false;
      }
      return true;
    })
    .map(q => mapToMeta(q));
}

function mapToGenerated(q: ArchiveQuestion): GeneratedQuestion {
  if (q.kind === 'mcq') {
    return {
      kind: 'mcq' as const,
      prompt: q.prompt,
      options: q.options ?? [],
      correctAnswer: typeof q.correctAnswer === 'number' ? q.correctAnswer : 0,
      explanation: q.explanation,
      kp: q.kp,
    };
  } else {
    return {
      kind: 'fill' as const,
      prompt: q.prompt,
      correctAnswer: String(q.correctAnswer),
      acceptedAnswers: q.acceptedAnswers,
      explanation: q.explanation,
      kp: q.kp,
    };
  }
}

function mapToMeta(q: ArchiveQuestion): ArchiveQuestionWithMeta {
  return {
    kind: q.kind,
    prompt: q.prompt,
    explanation: q.explanation,
    kp: q.kp,
    options: q.options,
    correctAnswer: q.correctAnswer,
    acceptedAnswers: q.acceptedAnswers,
    source: q.source ?? 'manual',
    sourceLabel: q.sourceLabel,
  };
}
